import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';
import { TEMPLATE_COLOR_VAR_PREFIX } from '@lumen5/luminary';

import { formatAsset } from '../video-state/serialization/assetFormatters';
import type { AssetDescription, SceneDescription } from '../video-state/types';

import { getSceneTranscriptTokens } from '../tools/transcript-partition';

import { intToHex } from 'js/src/libs/color-utils';
import type { RootState } from 'js/src/libs/RootState';
import { translateTranscriptTimeToSceneGroupTime } from 'js/src/luminary/luminary-selectors';

export interface TimedWord {
    word: string;
    startProgress: number;
    endProgress: number;
}

/** The words spanning a captured frame, with the one being spoken at that instant marked. */
export interface NarrationWindow {
    /** The words spanning the frame, by sequence order, boundaries inclusive. */
    words: TimedWord[];
    /** Index within `words` of the word being spoken at the frame, or -1 in a silent gap. */
    activeIndex: number;
}

/**
 * Describe a scene design's structure — its asset slots — for the design-browsing tools, in the same
 * asset vocabulary the agent reads for its own scene. The palette map comes from `luminary` (the
 * template's, so `var(--l5-color-N)` references resolve). Delegates to {@link formatDesignSlots}.
 */
export function formatSceneDesignStructure(
    description: SceneDescription,
    luminary: Luminary,
): string {
    return formatDesignSlots(description.assets, paletteVarByColorHex(luminary));
}

/**
 * Format a design's asset slots, palette-annotated. Unlike the agent's own-scene view this keeps
 * empty text slots — an unfilled headline/body slot *is* the design's layout — and only drops
 * zero-size assets, which never render. Pure over its inputs so it's unit-testable without Luminary.
 */
export function formatDesignSlots(
    assets: AssetDescription[],
    varByColorHex: Map<string, string>,
): string {
    const slots = assets.filter(hasRenderableBounds);
    if (!slots.length) {
        return 'Design structure: no visible slots.';
    }
    const slotsText = annotatePaletteVars(
        slots
            .map((asset) => formatAsset(asset))
            .join('')
            .trimEnd(),
        varByColorHex,
    );
    return `Design structure — asset slots:\n${slotsText}`;
}

/**
 * The current scene's voiceover narration, tagged with when each word is spoken so the agent can
 * sync its timeline to the speech. Timings are the word's position as scene progress (0–100%) —
 * the unit the agent authors in, since the host maps its timeline across the scene. Returns '' for
 * a scene with no narration (e.g. text-on-media) so the section is omitted; falls back to plain
 * text when the scene isn't voicedriven and word timings can't be mapped.
 */
export function formatSceneNarration(scene: Scene, state: RootState): string {
    const spoken = (getSceneTranscriptTokens(state, scene) ?? []).filter((token) => !token.isPause);
    if (!spoken.length) {
        return '';
    }
    const timed = timeNarrationWords(scene, spoken);
    return timed ? formatTimedNarration(timed) : formatNarration(narrationText(spoken));
}

function narrationText(spoken: ReadonlyArray<{ word: string }>): string {
    return spoken.map((token) => token.word).join(' ');
}

/**
 * Map each narration word's start and end onto its position within the scene, as progress (0–1).
 * Words arrive in transcript (source-audio) time; convert to scene-group time — which is cut-aware —
 * then rebase against the scene's own window. Returns null when the scene group has no playback
 * regions to map against, so the caller can fall back to plain text.
 */
export function timeNarrationWords(
    scene: Scene,
    words: ReadonlyArray<{ word: string; startTime: number; endTime: number }>,
): TimedWord[] | null {
    const sceneGroup = scene.sceneGroup;
    const regions = sceneGroup?.voicedrivenAssetPlaybackRegions;
    const baseDuration = sceneGroup?.voicedrivenAssetDuration;
    if (!regions?.length || !baseDuration || scene.duration <= 0) {
        return null;
    }
    const sceneStart = scene.startTimeRelativeToSceneGroup;
    const toProgress = (transcriptTime: number): number => {
        const sceneGroupTime = translateTranscriptTimeToSceneGroupTime(
            transcriptTime,
            regions,
            baseDuration,
        );
        const progress = (sceneGroupTime - sceneStart) / scene.duration;
        return Math.min(1, Math.max(0, progress));
    };
    return words.map(({ word, startTime, endTime }) => ({
        word,
        startProgress: toProgress(startTime),
        endProgress: toProgress(endTime),
    }));
}

/**
 * The narration around a frame at scene progress `P`: the word that most recently finished (largest
 * endProgress ≤ P), the first word not yet started (smallest startProgress > P), and every word
 * between them inclusive. Clamps to the first/last word when the frame precedes or follows all
 * speech, so the window is never empty when the scene has words. `activeIndex` marks the word
 * actually being spoken at `P`, or -1 when `P` lands in a silence between words. Null only when
 * there are no words. Pure over the timed words.
 */
export function narrationWindowAt(timed: TimedWord[], progress: number): NarrationWindow | null {
    if (!timed.length) {
        return null;
    }
    let firstIndex: number | null = null;
    let firstEnd = -Infinity;
    let lastIndex: number | null = null;
    let lastStart = Infinity;
    timed.forEach((word, index) => {
        if (word.endProgress <= progress && word.endProgress > firstEnd) {
            firstEnd = word.endProgress;
            firstIndex = index;
        }
        if (word.startProgress > progress && word.startProgress < lastStart) {
            lastStart = word.startProgress;
            lastIndex = index;
        }
    });
    const start = firstIndex ?? 0;
    const end = lastIndex ?? timed.length - 1;
    const words = timed.slice(start, end + 1);
    const activeIndex = words.findIndex(
        (word) => word.startProgress <= progress && progress < word.endProgress,
    );
    return { words, activeIndex };
}

/**
 * Render a narration window as its words space-joined, wrapping the currently-spoken word in `**` so
 * the model can tell "now" from the flanking context. A silent gap (no active word) is left unmarked.
 */
export function formatNarrationWindow(window: NarrationWindow): string {
    return window.words
        .map((word, index) => (index === window.activeIndex ? `**${word.word}**` : word.word))
        .join(' ');
}

/**
 * Render timed narration as inline `[NN%] word` cues (one scene's worth), so the agent can read the
 * sentence and see exactly when each word lands as scene progress. Pure over the timed words.
 */
export function formatTimedNarration(
    words: ReadonlyArray<{ word: string; startProgress: number }>,
): string {
    const line = words
        .map(({ word, startProgress }) => `[${Math.round(startProgress * 100)}%] ${word}`)
        .join(' ');
    return `## Narration during this scene ([NN%] marks when each word is spoken as scene progress — sync your timeline to it)\n${line}`;
}

/**
 * Plain (untimed) narration, the fallback when word timings can't be mapped. Pure over the string;
 * returns '' when empty so the section is omitted.
 */
export function formatNarration(narration: string): string {
    const trimmed = narration.trim();
    if (!trimmed) {
        return '';
    }
    return `## Narration during this scene (the voiceover spoken while your graphic is on screen — design to complement it)\n"${trimmed}"`;
}

/** An asset with no area doesn't render, wherever it's positioned. `bounds` is "x y width height". */
function hasRenderableBounds(asset: AssetDescription): boolean {
    const [, , width, height] = asset.bounds.split(' ').map(Number);
    return !(width === 0 && height === 0);
}

/** Maps a template color's hex string to the `--l5-color-N` variable it's injected as. */
function paletteVarByColorHex(luminary: Luminary): Map<string, string> {
    const map = new Map<string, string>();
    (luminary.template?.colors ?? []).forEach((templateColor, index) => {
        const hex = intToHex(templateColor.color);
        if (!map.has(hex)) {
            map.set(hex, `${TEMPLATE_COLOR_VAR_PREFIX}${index}`);
        }
    });
    return map;
}

/** Serialized asset lines whose value is a color (so `#hex` tokens on them are colors). */
const COLOR_FIELD_LINE = /^\s*(?:color|colors|iconColors):/;

/**
 * Annotate `#hex` color mentions (with any trailing ` (name)` left in place) with the palette
 * variable carrying that color, so the agent references `var(--l5-color-N)` instead of hardcoding
 * the hex. Scoped to color-property lines only, so a `#hex` appearing in some other field (e.g. a
 * media description) is left untouched.
 */
export function annotatePaletteVars(text: string, varByColorHex: Map<string, string>): string {
    return text
        .split('\n')
        .map((line) => {
            if (!COLOR_FIELD_LINE.test(line)) {
                return line;
            }
            return line.replace(/#[0-9a-f]{6}(?: \([^)]*\))?/gi, (match) => {
                const hex = match.slice(0, 7).toLowerCase();
                const variable = varByColorHex.get(hex);
                return variable ? `${match} = var(${variable})` : match;
            });
        })
        .join('\n');
}
